import React from 'react';
import useAuthContext from '../../hooks/useAuthContext';
import { Title, Loader, ReloadIconButton, TablePaginationWrapper } from '../../elements';
import FarmSettingsOptionsModal from '../FarmSettingsOptionsModal';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  Grid,
  Button,
  Icon,
  Chip,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  Table,
  Tooltip,
  IconButton,
  TableHead,
  Collapse,
  Card,
  CardHeader,
  Avatar,
  CardContent,
  FormControlLabel,
  Checkbox
} from '@material-ui/core';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { TasksService } from '../../hooks';
import { TaskListItem, TaskListViewModel } from '../../models/tasks';
import { AnimalType } from '../../models/animal';
import { LocationStable } from '../../models/locations';
import { CreateTaskListModal } from '../../components';
import UserRole from '../../constants/user-role';
import { TaskListItemStatus, TaskListItemType, TaskListStatus } from '../../models/common';

const TaskLists: React.FC<any> = () => {
  const classes = useStyles();
  const tasksService = new TasksService();
  const { state } = useAuthContext();
  const { t } = useTranslation();

  const [taskLists, setTaskLists] = React.useState([] as TaskListViewModel[]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [createTaskListModalOpened, setCreateTaskListModalOpened] = React.useState(false);

  const [assignedToMe, setAssignedToMe] = React.useState(false);
  const [includeCompleted, setIncludeCompleted] = React.useState(true);

  const loadTaskLists = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    tasksService
      .getTaskLists(searchPage, rowsPerPage, includeCompleted, assignedToMe)
      .then((result) => {
        setTaskLists(result.data);
        setTotal(result.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadTaskLists();
      loadData();
    }
  }, [loaded]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const handleCreateTaskListClose = (result: boolean) => {
    if (result) {
      setLoaded(false);
    }
    setCreateTaskListModalOpened(false);
  };

  const renderStatusCell = (taskList: TaskListViewModel) => {
    switch (taskList.status) {
      case TaskListStatus.Done:
        return (
          <Chip
            variant="outlined"
            className={classes.greenChip}
            icon={<Icon>{'check'}</Icon>}
            label={new Date(taskList.completedAt).toLocaleString()}
          />
        );

      case TaskListStatus.InProgress:
        return (
          <Chip
            variant="outlined"
            className={classes.orangeChip}
            icon={<Icon>{'hourglass_empty'}</Icon>}
            label={t('f-tasks-lists-items-progress')}
          />
        );

      case TaskListStatus.ToDo:
        return <Chip variant="outlined" icon={<Icon>{'alarm'}</Icon>} label={t('f-tasks-lists-items-todo')} />;
    }
  };

  function TaskListTableRow(props: { taskList: TaskListViewModel }) {
    const { taskList } = props;
    const [rowExpanded, setRowExpanded] = React.useState(false);

    return (
      <React.Fragment key={taskList.id}>
        <TableRow key={taskList.id} hover selected={rowExpanded}>
          <TableCell>
            <Tooltip arrow title={rowExpanded ? t('f-com-hide') : t('f-com-expand')}>
              <IconButton aria-label="expand row" size="small" onClick={() => setRowExpanded(!rowExpanded)}>
                {rowExpanded ? <Icon>{'keyboard_arrow_up'}</Icon> : <Icon>{'keyboard_arrow_down'}</Icon>}
              </IconButton>
            </Tooltip>
          </TableCell>

          <TableCell>{taskList.name}</TableCell>
          <TableCell>{new Date(taskList.created).toLocaleString()}</TableCell>
          <TableCell>{taskList.user?.name || '-'}</TableCell>
          <TableCell align="center">{renderStatusCell(taskList)}</TableCell>
        </TableRow>

        <TableRow key={`row${taskList.id}`}>
          <TableCell className={classes.tableRowArrowCell} colSpan={5}>
            <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
              {renderTaskListDetailsSection(taskList)}
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  const renderTaskListDetailsSection = (taskList: TaskListViewModel) => {
    return (
      <Grid container direction="column">
        {/* <Grid item>
          <Typography variant="h6">{t('f-tasks-lists-items')}</Typography>
        </Grid> */}

        {taskList.taskListItems && (
          <Grid item container direction="column">
            {taskList.taskListItems.map((item, index) => (
              <Grid item key={index}>
                <TaskListItemCard item={item} key={index} />
              </Grid>
            ))}
          </Grid>
        )}

        {!taskList.taskListItems && <Chip className={classes.tableInfoChip} label={t('f-tasks-lists-items-empty')} />}
      </Grid>
    );
  };

  const renderTaskListItemAvatar = (item: TaskListItem) => {
    switch (item.type) {
      case TaskListItemType.SickAnimals:
        return (
          <Avatar>
            <Icon>{'coronavirus'}</Icon>
          </Avatar>
        );

      case TaskListItemType.Injections:
        return (
          <Avatar>
            <Icon>{'healing'}</Icon>
          </Avatar>
        );

      default:
        return (
          <Avatar>
            <Icon>{'help_outline'}</Icon>
          </Avatar>
        );
    }
  };

  const renderTaskListItemCardAction = (item: TaskListItem) => {
    switch (item.status) {
      case TaskListItemStatus.Done:
        return (
          <Chip
            variant="outlined"
            className={classes.greenChip}
            icon={<Icon>{'check'}</Icon>}
            label={`${item.completedCount} / ${item.totalCount}`}
          />
        );

      case TaskListItemStatus.Partial:
        return (
          <Chip
            variant="outlined"
            className={classes.orangeChip}
            icon={<Icon>{'tonality'}</Icon>}
            label={`${item.completedCount} / ${item.totalCount}`}
          />
        );

      case TaskListItemStatus.InProgress:
        return (
          <Chip
            variant="outlined"
            icon={<Icon>{'hourglass_empty'}</Icon>}
            label={`${item.completedCount} / ${item.totalCount}`}
          />
        );
    }
  };

  function TaskListItemCard(props: { item: TaskListItem }) {
    const { item } = props;
    const [page, setPage] = React.useState(0);
    const rowsPerPage = 10;
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
    };

    return (
      <Card className={classes.withMarginTopBottom} variant="outlined">
        <CardHeader
          avatar={renderTaskListItemAvatar(item)}
          subheader={`${t('f-com-animal-type')}: ${AnimalType[item.animalType]} | ${t('f-loc-stable')}: ${
            LocationStable[item.stableType]
          }`}
          action={renderTaskListItemCardAction(item)}
          title={`${TaskListItemType[item.type]}`}
        />

        {item.subtasks && (
          <CardContent>
            <TablePaginationWrapper
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={item.subtasks.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
            >
              <Table size="small">
                <TableBody>
                  {item.subtasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((sub, index) => (
                    <TableRow key={`sub${index}`}>
                      <TableCell>{sub.number}</TableCell>
                      {sub.ageDays && <TableCell>{t('f-com-age-days', { age: sub.ageDays })}</TableCell>}
                      <TableCell>{sub.location || '-'}</TableCell>
                      <TableCell>{sub.medicalSupply}</TableCell>
                      <TableCell>{`${sub.completedDose.toFixed(2)} of ${sub.dose.toFixed(2)} CC`}</TableCell>
                      <TableCell>{sub.completed ? <Icon>{'check'}</Icon> : <Icon>{'hourglass_empty'}</Icon>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TablePaginationWrapper>
          </CardContent>
        )}
      </Card>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          {/* title & spinner */}
          <Grid container direction="row" className={classes.paperHeader}>
            <Grid item container md={6} xs={10} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'add_task'}
                </Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-tasks-lists')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>

            <Grid item container className={classes.justifyRight} md={6} xs={10}>
              <Grid item>
                <ReloadIconButton action={() => setLoaded(false)} />
              </Grid>

              <Grid item>
                <FarmSettingsOptionsModal />
              </Grid>

              <Grid item>
                <Button
                  disabled={state.role === UserRole.user}
                  id="addTaskListBtn"
                  className={classes.withMarginLeft}
                  color="default"
                  size="small"
                  variant="contained"
                  startIcon={<Icon>{'add_circle_outline'}</Icon>}
                  onClick={() => setCreateTaskListModalOpened(true)}
                >
                  {t('f-tasks-lists-add-btn')}
                </Button>
              </Grid>
            </Grid>

            {/* filters section */}
            <Grid item md={12} container spacing={1}>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeCompleted}
                      color="primary"
                      onChange={(e) => {
                        setIncludeCompleted(e.target.checked);
                        setLoaded(false);
                      }}
                    />
                  }
                  label={t('f-tasks-lists-include-compl')}
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={assignedToMe}
                      color="primary"
                      onChange={(e) => {
                        setAssignedToMe(e.target.checked);
                        setLoaded(false);
                      }}
                    />
                  }
                  label={t('f-tasks-lists-assigned-to-me')}
                />
              </Grid>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {/* data table */}
          {loaded && (
            <Grid container spacing={3}>
              <Grid item md={12}>
                <TablePaginationWrapper
                  rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                >
                  <Paper elevation={1}>
                    <Table size="small" className={classes.diseasesTable}>
                      <TableHead className={classes.tableHeader}>
                        <TableRow>
                          <TableCell />
                          <TableCell>{t('f-com-name')}</TableCell>
                          <TableCell>{t('f-com-created')}</TableCell>
                          <TableCell>{t('f-tasks-lists-assigned-to')}</TableCell>
                          <TableCell align="center">{t('f-com-status')}</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {taskLists.map((taskList, index) => (
                          <TaskListTableRow key={index} taskList={taskList} />
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </TablePaginationWrapper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <CreateTaskListModal opened={createTaskListModalOpened} onModalAction={handleCreateTaskListClose} />
    </>
  );
};

export default TaskLists;
